import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * HPS: badge text -> CSS modifier. Keep in sync with the case statement in
 * snippets/sku.liquid. Styles live in assets/hps-custom.css.
 * @type {Record<string, string>}
 */
const BADGE_CLASSES = {
  'New': 'badge--new',
  'Best Seller': 'badge--bestseller',
  'Exclusives': 'badge--exclusives',
  'Highly Recommended': 'badge--highlyrecommended',
  'Top 10': 'badge--top10',
  'Top10': 'badge--top10',
  'Open Box': 'badge--openbox',
  'Backorder': 'badge--backorder',
  'Pre-order': 'badge--preorder',
  'Clearance': 'badge--clearance',
  'Last Chance': 'badge--clearance',
  'Discontinued': 'badge--discontinued',
  'Coming Soon': 'badge--coming-soon',
};

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * HPS: also shows the variant status badge (variant metafield custom.badge),
 * read from window.productVariantData (written by snippets/variant-main-picker.liquid).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 * @property {HTMLElement} [badgeContainer] - HPS: the container for the badge
 * @property {HTMLElement} [badgeValue] - HPS: the element that holds the badge
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * HPS: looks up variant data written by variant-main-picker.liquid.
   * @param {string | number | undefined} productId
   * @param {string | number | undefined} variantId
   * @returns {{ sku?: string, badge?: string } | null}
   */
  #getVariantData(productId, variantId) {
    if (productId == null || variantId == null) return null;
    return window.productVariantData?.[String(productId)]?.variants?.[String(variantId)] ?? null;
  }

  /**
   * HPS: renders the badge pill (built with DOM APIs, so badge text is never parsed as HTML).
   * @param {string} badgeText
   */
  #renderBadge(badgeText) {
    const { badgeContainer, badgeValue } = this.refs;
    if (!badgeContainer || !badgeValue) return;

    if (!badgeText) {
      badgeContainer.style.display = 'none';
      badgeValue.replaceChildren();
      return;
    }

    const badge = document.createElement('span');
    badge.className = ['badge', BADGE_CLASSES[badgeText]].filter(Boolean).join(' ');
    badge.textContent = badgeText === 'Top10' ? 'Top 10' : badgeText;
    badgeValue.replaceChildren(badge);
    badgeContainer.style.display = 'flex';
  }

  /**
   * Handles product select event and updates the SKU.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (resource) {
          const variantData = this.#getVariantData(this.dataset.productId, resource.id);
          const variantSku = variantData?.sku || resource.sku || '';
          const badgeText = variantData?.badge || '';

          this.refs.sku.textContent = variantSku;
          this.#renderBadge(badgeText);

          // HPS: keep the block visible when there is a badge even without a SKU.
          this.style.display = variantSku || badgeText ? 'block' : 'none';
        } else {
          // HPS: the selected option combination does not exist — don't keep showing
          // the previous variant's part number and badge.
          this.refs.sku.textContent = '';
          this.#renderBadge('');
          this.style.display = 'none';
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
